import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { useAuth } from '../contexts/AuthContext'
import { getImageUrl } from '../utils/cdnUtils'
import { pendingDepositDraftService, type PendingDepositDraft } from '../services/pendingDepositDraftService'
import { walletTransactionService, type WalletTransaction } from '../services/walletTransactionService'
import { getTenantId } from '../utils/tenantUtils'
import { translateDepositReviewReason } from '../utils/depositReviewReason'

type TransactionTab = 'all' | 'deposit' | 'withdrawal'

interface Transaction {
  id: string
  type: 'deposit' | 'withdrawal'
  amount: number
  status: 'approved' | 'pending' | 'rejected' | 'processing' | 'draft'
  date: string
  bankName: string
  accountName: string
  accountNumber: string
  note: string
  actionLabel?: string
  onAction?: () => void
  onSecondaryAction?: () => void
  secondaryActionLabel?: string
}

const normalizeTransactionStatus = (status?: string): Transaction['status'] => {
  switch (status) {
    case 'verified':
    case 'completed':
      return 'approved'
    case 'failed':
    case 'cancelled':
    case 'expired':
      return 'rejected'
    case 'processing':
    case 'reserved':
    case 'locked':
      return 'processing'
    case 'pending':
      return 'pending'
    default:
      return 'pending'
  }
}

const TransactionPage: React.FC = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const userRecord = user as (typeof user & { id?: string; _id?: string }) | null
  const userId = userRecord?.id || userRecord?._id || ''
  const [activeTab, setActiveTab] = useState<TransactionTab>('all')
  const [pendingDrafts, setPendingDrafts] = useState<PendingDepositDraft[]>([])
  const [depositTransactions, setDepositTransactions] = useState<WalletTransaction[]>([])
  const [withdrawalTransactions, setWithdrawalTransactions] = useState<WalletTransaction[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [errorMessage, setErrorMessage] = useState('')
  const tenantId = user?.tenantId || getTenantId(user || undefined) || ''

  const loadTransactions = useCallback(async () => {
    if (!tenantId || !userId) {
      setPendingDrafts([])
      setDepositTransactions([])
      setWithdrawalTransactions([])
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    setErrorMessage('')
    setPendingDrafts(pendingDepositDraftService.list(tenantId, userId))

    const depositResponse = await walletTransactionService.getUserTransactions({
      type: 'deposit',
      limit: 50,
    })
    const withdrawalResponse = await walletTransactionService.getUserTransactions({
      type: 'withdraw',
      limit: 50,
    })

    if (depositResponse.success) {
      setDepositTransactions(depositResponse.data?.transactions || [])
    } else {
      setDepositTransactions([])
      setErrorMessage(depositResponse.message || 'ไม่สามารถโหลดรายการฝากเงินได้')
    }

    if (withdrawalResponse.success) {
      setWithdrawalTransactions(withdrawalResponse.data?.transactions || [])
    } else {
      setWithdrawalTransactions([])
      setErrorMessage((current) => current || withdrawalResponse.message || 'ไม่สามารถโหลดรายการถอนเงินได้')
    }

    setIsLoading(false)
  }, [tenantId, userId])

  useEffect(() => {
    loadTransactions()
  }, [loadTransactions])

  const handleContinueDraft = useCallback((draftId: string) => {
    navigate(`/topup-confirm?draft=${encodeURIComponent(draftId)}`)
  }, [navigate])

  const handleRemoveDraft = useCallback((draftId: string) => {
    if (!tenantId || !userId) return
    pendingDepositDraftService.remove(tenantId, userId, draftId)
    setPendingDrafts((current) => current.filter((draft) => draft.id !== draftId))
    toast.success('ลบรายการฝากค้างเรียบร้อยแล้ว')
  }, [tenantId, userId])

  const transactions = useMemo<Transaction[]>(() => {
    const draftTransactions = pendingDrafts.map((draft) => ({
      id: draft.id,
      type: 'deposit' as const,
      amount: Number(draft.amount) || 0,
      status: 'draft' as const,
      date: draft.updatedAt,
      bankName: draft.depositAccount?.bankName || 'บัญชีฝาก',
      accountName: draft.depositAccount?.accountName || '-',
      accountNumber: draft.depositAccount?.accountNumber || '-',
      note: 'รายการฝากค้าง รอแนบสลิป',
      actionLabel: 'แนบสลิปต่อ',
      onAction: () => handleContinueDraft(draft.id),
      secondaryActionLabel: 'ลบรายการ',
      onSecondaryAction: () => handleRemoveDraft(draft.id),
    }))

    const depositHistoryTransactions = depositTransactions.map((transaction) => {
      const normalizedStatus = normalizeTransactionStatus(transaction.status)
      const depositMethod = String(transaction.metadata?.depositMethod || '').trim().toLowerCase()
      const sourceChannel = String(transaction.metadata?.depositRouting?.sourceChannel || '').trim().toLowerCase()
      const bankName =
        transaction.metadata?.selectedDepositBankName ||
        transaction.details?.bankAccount?.bankCode ||
        (depositMethod === 'true_money'
          ? 'TrueMoney'
          : depositMethod === 'prompt_pay'
            ? 'พร้อมเพย์'
            : depositMethod === 'decimal_reservation' || sourceChannel === 'payment'
              ? 'Payment Gateway'
              : 'ฝากเงิน')

      return {
        id: transaction._id,
        type: 'deposit' as const,
        amount: Number(transaction.amount || 0),
        status: normalizedStatus,
        date: String(transaction.audit?.createdAt || transaction.createdAt || transaction.audit?.updatedAt || transaction.updatedAt || ''),
        bankName,
        accountName: transaction.metadata?.selectedDepositAccountName || transaction.details?.bankAccount?.accountName || user?.username || '-',
        accountNumber: transaction.metadata?.selectedDepositAccountNumber || transaction.details?.bankAccount?.accountNumber || transaction.transactionId || '-',
        note: translateDepositReviewReason(
          transaction.metadata?.depositRouting?.reviewReason ||
            transaction.metadata?.notes ||
            transaction.details?.description ||
            transaction.details?.reference ||
            transaction.transactionId ||
            null,
          'รายการฝากเงิน'
        ),
      }
    })

    const withdrawalHistoryTransactions = withdrawalTransactions.map((transaction) => ({
      id: transaction._id,
      type: 'withdrawal' as const,
      amount: -(transaction.amount || 0),
      status: normalizeTransactionStatus(transaction.status),
      date: String(transaction.audit?.createdAt || transaction.createdAt || transaction.audit?.updatedAt || transaction.updatedAt || ''),
      bankName: transaction.details?.bankAccount?.bankCode || 'ถอนเงิน',
      accountName: transaction.details?.bankAccount?.accountName || user?.username || '-',
      accountNumber: transaction.details?.bankAccount?.accountNumber || transaction.transactionId || '-',
      note: translateDepositReviewReason(
        transaction.details?.description ||
          transaction.details?.reference ||
          transaction.transactionId ||
          null,
        'รายการถอนเงิน'
      ),
    }))

    return [...draftTransactions, ...depositHistoryTransactions, ...withdrawalHistoryTransactions].sort(
      (a, b) => Date.parse(b.date) - Date.parse(a.date)
    )
  }, [depositTransactions, handleContinueDraft, handleRemoveDraft, pendingDrafts, user?.username, withdrawalTransactions])

  const filteredTransactions = useMemo(() => {
    if (activeTab === 'all') return transactions
    if (activeTab === 'withdrawal') {
      return transactions.filter((transaction) => transaction.type === 'withdrawal')
    }
    return transactions.filter((transaction) => transaction.type === 'deposit')
  }, [activeTab, transactions])

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Math.abs(amount))
  }

  const formatDate = (value: string) => {
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return value
    return new Intl.DateTimeFormat('th-TH', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(date)
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft': return 'bgc-5'
      case 'approved': return 'bgc-11'
      case 'pending': return 'bgc-9'
      case 'processing': return 'bgc-5'
      case 'rejected': return 'bgc-2'
      default: return 'bgc-11'
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'draft': return 'รอแนบสลิป'
      case 'approved': return 'อนุมัติ'
      case 'pending': return 'รอดำเนินการ'
      case 'processing': return 'กำลังตรวจสอบ'
      case 'rejected': return 'ปฏิเสธ'
      default: return 'รอดำเนินการ'
    }
  }

  const getTypeColor = (type: string) => {
    return type === 'deposit' ? 'bgc-11' : 'bgc-9'
  }

  const getTypeText = (type: string) => {
    return type === 'deposit' ? 'ฝากเครดิต' : 'ถอนเครดิต'
  }

  const getAmountColor = (amount: number) => {
    return amount > 0 ? 'c-green' : 'c-red'
  }

  return (
    <div className="content pb-3">
      <div className="container">
        <div className="row px--1">
          <div className="col-12 px-1">
            <div className="bdr-5 bx-sd mb-2">
              <div className="row mx--1">
                <div className="col-12 px-1">
                  <div className="w-100 p-2 bgc-4 wrap justify-content-between">
                    <a 
                      href="#" 
                      className="txt-link-d"
                      onClick={(e) => {
                        e.preventDefault()
                        navigate('/dashboard')
                      }}
                    >
                      <i className="fas fa-chevron-left me-1"></i>
                      หน้าหลัก
                    </a>
                  </div>
                </div>

                <div className="col-12 px-1">
                  <div className="w-100 p-2 bgc-7">
                    <div className="bdr-5 bgc-4 c-red px-2 py-1 fs-18 t-medium mb-2">
                      <i className="fas fa-wallet me-2"></i>
                      สถานะฝาก-ถอน
                    </div>

                    <div className="row mx--1">
                      <div className="col-4 px-1 mb-2">
                        <a 
                          href="#" 
                          className={`check-button w-100 ${activeTab === 'all' ? 'bgc-5' : 'bgc-5'} c-white`}
                          onClick={(e) => {
                            e.preventDefault()
                            setActiveTab('all')
                          }}
                        >
                          <i className="fas fa-sort-numeric-up-alt me-2"></i>
                          ทั้งหมด
                        </a>
                      </div>
                      <div className="col-4 px-1 mb-2">
                        <a 
                          href="#" 
                          className={`check-button w-100 ${activeTab === 'deposit' ? 'bgc-11' : 'bgc-11'} c-white`}
                          onClick={(e) => {
                            e.preventDefault()
                            setActiveTab('deposit')
                          }}
                        >
                          <i className="fas fa-caret-right me-2"></i>
                          ฝาก
                        </a>
                      </div>
                      <div className="col-4 px-1 mb-2">
                        <a 
                          href="#" 
                          className={`check-button w-100 ${activeTab === 'withdrawal' ? 'bgc-9' : 'bgc-9'} c-white`}
                          onClick={(e) => {
                            e.preventDefault()
                            setActiveTab('withdrawal')
                          }}
                        >
                          <i className="fas fa-caret-left me-2"></i>
                          ถอน
                        </a>
                      </div>
                    </div>

                    <div className="bdr-5 bgc-4 p-2">
                      {errorMessage && (
                        <div className="alert alert-warning mb-2">
                          <i className="fas fa-exclamation-triangle me-1"></i>
                          {errorMessage}
                        </div>
                      )}
                      {pendingDrafts.length > 0 && activeTab !== 'withdrawal' && (
                        <div className="alert alert-info mb-2 fs-12">
                          <i className="fas fa-history me-1"></i>
                          หากเผลอออกจากหน้าแจ้งโอน ระบบจะเก็บรายการฝากค้างไว้ให้กลับมาแนบสลิปต่อได้จากหน้านี้
                        </div>
                      )}
                      <div className="row mx--1" style={{ rowGap: '0.5rem' }}>
                        {isLoading ? (
                          <div className="col-12 px-1">
                            <h5 className="c-dark text-center">กำลังโหลดข้อมูล...</h5>
                          </div>
                        ) : filteredTransactions.length > 0 ? (
                          filteredTransactions.map((transaction) => (
                            <div key={transaction.id} className="col-12 col-md-6 px-1">
                              <div className="bdr-5 bdc-2 bgc-4">
                                <div
                                  className="d-grid bgg-1 gap-2 align-items-center p-2"
                                  style={{ gridTemplateColumns: 'auto 1fr auto' }}
                                >
                                  <div
                                    className="bgc-4 bdr-30 wrap justify-content-center rounded-circle bx-sd overflow-hidden"
                                    style={{ width: '34px', height: '34px' }}
                                  >
                                    <img
                                      src={getImageUrl('icons-kbank.png')}
                                      alt=""
                                    />
                                  </div>
                                  <div className="text-center">
                                    <h6 className="t-medium c-white">{transaction.bankName}</h6>
                                  </div>
                                  <div
                                    className={`${getStatusColor(transaction.status)} bdr-5 wrap justify-content-center p-1 text-center c-white ms-auto`}
                                    style={{ height: 'fit-content' }}
                                  >
                                    <small>{getStatusText(transaction.status)}</small>
                                  </div>
                                </div>

                                <div className="px-1 py-2 d-grid gap-2">
                                  <div
                                    className="d-grid gap-2 align-items-center"
                                    style={{
                                      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))'
                                    }}
                                  >
                                    <div className="wrap justify-content-center gap-2">
                                      <h6
                                        className={`${getTypeColor(transaction.type)} bdr-5 px-1 text-center c-white`}
                                      >
                                        {getTypeText(transaction.type)}
                                      </h6>
                                      {transaction.status === 'draft' && (
                                        <h6 className="bgc-4 bdr-5 px-1 text-center c-yellow t-medium">
                                          ยังไม่ส่งสลิป
                                        </h6>
                                      )}
                                      <h6
                                        className={`bgc-4 bdr-5 px-1 text-center ${getAmountColor(transaction.amount)} t-medium`}
                                      >
                                        {transaction.amount > 0 ? '+' : ''}{formatCurrency(transaction.amount)}
                                      </h6>
                                    </div>

                                    <h6 className="text-center">{formatDate(transaction.date)}</h6>
                                  </div>

                                  <div
                                    className="d-grid bgg-2 bdr-5 gap-2 align-items-center p-1"
                                    style={{
                                      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))'
                                    }}
                                  >
                                    <div className="d-grid">
                                      <small className="c-dark t-medium">ชื่อบัญชี:</small>
                                      <small className="c-dark">{transaction.accountName}</small>
                                    </div>

                                    <div className="d-grid text-end">
                                      <small className="c-dark t-medium">เลขที่บัญชี:</small>
                                      <small className="c-dark">{transaction.accountNumber}</small>
                                    </div>
                                  </div>

                                  <div className="text-center">
                                    <h5 className="c-red t-medium">หมายเหตุ:</h5>
                                    <small className="c-dark" style={{ fontSize: '12px' }}>
                                      {transaction.note}
                                    </small>
                                  </div>

                                  {(transaction.onAction || transaction.onSecondaryAction) && (
                                    <div className="d-flex gap-2 justify-content-center flex-wrap">
                                      {transaction.onAction && (
                                        <button
                                          type="button"
                                          className="check-button bgc-11 c-white border-0 px-3 py-2"
                                          onClick={transaction.onAction}
                                        >
                                          {transaction.actionLabel}
                                        </button>
                                      )}
                                      {transaction.onSecondaryAction && (
                                        <button
                                          type="button"
                                          className="btn btn-outline-danger btn-sm"
                                          onClick={transaction.onSecondaryAction}
                                        >
                                          {transaction.secondaryActionLabel}
                                        </button>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="col-12 px-1">
                            <h5 className="c-red text-center">ไม่มีข้อมูล</h5>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TransactionPage
